import { useMemo, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { fetchModuleMappings, hasSubModulePermission, isAdmin } from '../../utils/permissions'
import axiosInstance from '../../api/axiosInstance'
import Select from 'react-select'

const COLORS = { gray: '#5B6B79', blue: '#96C5F7', green: '#2CA87F', red: '#9E2B25' }

// Lets long patient / doctor / test names break onto extra lines instead of colliding
// with the neighbouring column under `tableLayout: fixed`.
const WRAP = { whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }

const PERM_KEY = { general: 'generalReports', laboratory: 'laboratoryReports', medical: 'medicalReports', financial: 'financialReports', daily: 'dailyReports' }

const NavCard = ({ icon, title, disabled, onClick }) => (
  <div
    onClick={disabled ? undefined : onClick}
    style={{
      background: '#fff', border: `1px solid ${disabled ? '#f0f2f5' : '#e8ecf0'}`, borderRadius: 12,
      padding: '24px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1,
      transition: 'box-shadow 0.15s, border-color 0.15s, transform 0.1s', userSelect: 'none',
    }}
    onMouseEnter={e => { if (!disabled) { e.currentTarget.style.boxShadow = '0 4px 16px rgba(150,197,247,0.25)'; e.currentTarget.style.borderColor = COLORS.blue; e.currentTarget.style.transform = 'translateY(-2px)' } }}
    onMouseLeave={e => { if (!disabled) { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e8ecf0'; e.currentTarget.style.transform = 'translateY(0)' } }}
  >
    <div style={{ width: 52, height: 52, borderRadius: 14, background: disabled ? '#f0f2f5' : `${COLORS.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <i className={`ti ${icon}`} style={{ fontSize: 24, color: disabled ? '#bbb' : COLORS.blue }} />
    </div>
    <span style={{ fontSize: 13, fontWeight: 600, color: disabled ? '#aaa' : '#1a202c', textAlign: 'center', lineHeight: 1.3 }}>{title}</span>
  </div>
)

export default function Reports() {
  const allBranchSettings = useSelector(state => state.business.allBranchSettings)
  const activeBranchId = useSelector(state => state.business.activeBranchId)

  const [showModal, setShowModal] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [downloading, setDownloading] = useState(null)
  const [financialBranchId, setFinancialBranchId] = useState('all')

  // VAT Report state
  const [vatModal, setVatModal] = useState(false)
  const [vatStart, setVatStart] = useState('')
  const [vatEnd, setVatEnd] = useState('')
  const [vatResults, setVatResults] = useState(null)
  const [vatLoading, setVatLoading] = useState(false)

  // By Provider state
  const [providerModal, setProviderModal] = useState(false)
  const [providerDoctors, setProviderDoctors] = useState([])
  const [providerDoctorId, setProviderDoctorId] = useState('')
  const [providerStart, setProviderStart] = useState('')
  const [providerEnd, setProviderEnd] = useState('')
  const [providerResults, setProviderResults] = useState(null)
  const [providerLoading, setProviderLoading] = useState(false)

  // Services Report state
  const [servicesModal, setServicesModal] = useState(false)
  const [servicesList, setServicesList] = useState([])
  const [servicesDoctors, setServicesDoctors] = useState([])
  const [svcStart, setSvcStart] = useState('')
  const [svcEnd, setSvcEnd] = useState('')
  const [svcServiceIds, setSvcServiceIds] = useState([])
  const [svcStatus, setSvcStatus] = useState('all')
  const [svcDoctorId, setSvcDoctorId] = useState('all')
  const [svcResults, setSvcResults] = useState(null)
  const [svcLoading, setSvcLoading] = useState(false)

  // Income Report state
  const [incomeModal, setIncomeModal] = useState(false)
  const [incomeDoctors, setIncomeDoctors] = useState([])
  const [incomeStart, setIncomeStart] = useState('')
  const [incomeEnd, setIncomeEnd] = useState('')
  const [incomeMethod, setIncomeMethod] = useState('all')
  const [incomeStatus, setIncomeStatus] = useState('all')
  const [incomeDoctorId, setIncomeDoctorId] = useState('all')
  const [incomeResults, setIncomeResults] = useState(null)
  const [incomeLoading, setIncomeLoading] = useState(false)

  // Invoices Report state
  const [invoicesModal, setInvoicesModal] = useState(false)
  const [invoicesUsers, setInvoicesUsers] = useState([])
  const [invoicesStart, setInvoicesStart] = useState('')
  const [invoicesEnd, setInvoicesEnd] = useState('')
  const [invoicesStatus, setInvoicesStatus] = useState('all')
  const [invoicesRecordStatus, setInvoicesRecordStatus] = useState('all')
  const [invoicesUserId, setInvoicesUserId] = useState('all')
  const [invoicesResults, setInvoicesResults] = useState(null)
  const [invoicesLoading, setInvoicesLoading] = useState(false)

  // Labs by Doctor Report state
  const [labDocModal, setLabDocModal] = useState(false)
  const [labDocDoctors, setLabDocDoctors] = useState([])
  const [labDocServices, setLabDocServices] = useState([])
  const [labDocStart, setLabDocStart] = useState('')
  const [labDocEnd, setLabDocEnd] = useState('')
  const [labDocDoctorId, setLabDocDoctorId] = useState('all')
  const [labDocServiceIds, setLabDocServiceIds] = useState([])
  const [labDocStatus, setLabDocStatus] = useState('all')
  const [labDocResults, setLabDocResults] = useState(null)
  const [labDocLoading, setLabDocLoading] = useState(false)

  const [permissions, setPermissions] = useState({
    generalReports: false, laboratoryReports: false, medicalReports: false,
    financialReports: false, dailyReports: false,
  })

  useEffect(() => {
    const checkPermissions = async () => {
      if (isAdmin()) {
        setPermissions({ generalReports: true, laboratoryReports: true, medicalReports: true, financialReports: true, dailyReports: true })
        return
      }
      try {
        const allModules = await fetchModuleMappings()
        const reportsModule = allModules.find(m => m.module_name.toLowerCase() === 'reports')
        if (reportsModule?.sub_modules) {
          const sm = reportsModule.sub_modules
          const p = name => hasSubModulePermission(sm.find(s => s.sub_module_name === name)?.id)
          setPermissions({
            generalReports: p('general_reports'), laboratoryReports: p('laboratory_reports'),
            medicalReports: p('medical_reports'), financialReports: p('financial_reports'),
            dailyReports: p('daily_reports'),
          })
        }
      } catch (err) {
        console.error('Error checking permissions', err)
      }
    }
    checkPermissions()
  }, [])

  const reportConfig = {
    appointments: { endpoint: 'dashboard/appointment_report', filename: 'appointment_report.xlsx' },
    patientList: { endpoint: 'dashboard/patients_list_report', filename: 'patient_list_report.xlsx' },
    pharmacyDaily: { endpoint: 'dashboard/rx_list_report', filename: 'pharmacy_report.xlsx' },
    testsReport: { endpoint: 'dashboard/lab_request_report', filename: 'lab_report.xlsx' },
    visitByGender: { endpoint: 'dashboard/patients_report_by_gender', filename: 'Gender_report.xlsx' },
    visitByAge: { endpoint: 'dashboard/patients_report_by_age', filename: 'Patient_age_report.xlsx' },
    testsByLab1: { endpoint: 'dashboard/patients_report_test_bylabs', filename: 'Reports_test_by_labs_report.xlsx' },
    mostUsedServices: { endpoint: 'dashboard/services_mostused_report', filename: 'Services_Most_Used_report.xlsx' },
    chiefComplaint: { endpoint: 'dashboard/chief_complaints_report', filename: 'chief_complaint_report.xlsx' },
    allergiesReport: { endpoint: 'dashboard/allergies_report', filename: 'Allergy_Report.xlsx' },
    diagnosisByPatient: { endpoint: 'dashboard/patients_diagnosis_report', filename: 'Patient_diagnosis_report.xlsx' },
    xrayReport: { endpoint: 'dashboard/patients_xray_reports', filename: 'xrayReport.xlsx' },
    doctorsDaily: { endpoint: 'dashboard/doctors_report', filename: 'Doctor_report.xlsx' },
    invoicesVat: null,
    invoicesDaily: { endpoint: 'dashboard/daily_invoices_report', filename: 'Daily_invoices_report.xlsx' },
    paymentsDaily: { endpoint: 'dashboard/daily_payments_report', filename: 'daily_payments_report.xlsx' },
  }

  const categories = useMemo(() => [
    {
      key: 'general', title: 'General Reports', icon: 'ti-clipboard-list',
      items: [
        { key: 'appointments', label: 'Appointment' },
        { key: 'patientList', label: 'Patient List Report' },
        { key: 'patientWithBalance', label: 'Patient with Balance' },
        { key: 'visitByGender', label: 'Visit by Gender' },
        { key: 'visitByAge', label: 'Visits by Age' },
      ],
    },
    {
      key: 'laboratory', title: 'Laboratory Reports', icon: 'ti-test-pipe',
      items: [
        { key: 'testsReport', label: 'Tests Report' },
        { key: 'testsByLab1', label: 'Tests by Lab' },
        { key: 'labsByDoctor', label: 'Labs by Doctor', special: true, specialType: 'labsByDoctor' },
      ],
    },
    {
      key: 'medical', title: 'Medical Reports', icon: 'ti-stethoscope',
      items: [
        { key: 'mostUsedServices', label: 'Most Used Services' },
        { key: 'chiefComplaint', label: 'Chief Complaint Report' },
        { key: 'diagnosisByPatient', label: 'Diagnosis By Patient' },
        { key: 'allergiesReport', label: 'Allergies Report' },
        { key: 'xrayReport', label: 'X-Ray Report' },
      ],
    },
    {
      key: 'financial', title: 'Financial Reports', icon: 'ti-file-invoice',
      items: [
        { key: 'invoices', label: 'Invoices Report', special: true, specialType: 'invoices' },
        { key: 'invoicesVat', label: 'VAT Report', special: true, specialType: 'vat' },
        { key: 'income', label: 'Income Report', special: true, specialType: 'income' },
        { key: 'servicesReport', label: 'Services Report', special: true, specialType: 'services' },
        { key: 'byProvider', label: 'By Provider', special: true },
      ],
    },
    {
      key: 'daily', title: 'Daily Reports', icon: 'ti-calendar-stats',
      items: [
        { key: 'paymentsDaily', label: 'Payments +' },
        { key: 'invoicesDaily', label: 'Invoices +' },
        { key: 'pharmacyDaily', label: 'Pharmacy +' },
        { key: 'doctorsDaily', label: 'Doctors +' },
      ],
    },
  ], [])

  const handleCardClick = cat => {
    setSelectedCategory(cat); setShowModal(true); setStartDate(''); setEndDate('')
    if (cat.key === 'financial') {
      const bid = activeBranchId || parseInt(localStorage.getItem('activeBranchId')) || null
      setFinancialBranchId(bid ? String(bid) : 'all')
    }
  }
  const closeModal = () => { setShowModal(false); setSelectedCategory(null) }

  const openVatModal = () => {
    setVatResults(null); setVatStart(''); setVatEnd('')
    setShowModal(false)
    setVatModal(true)
  }

  const runVatReport = async () => {
    if (!vatStart || !vatEnd) return
    setVatLoading(true); setVatResults(null)
    try {
      const params = { startDate: vatStart, endDate: vatEnd }
      if (financialBranchId && financialBranchId !== 'all') params.branchId = financialBranchId
      const res = await axiosInstance.get('getVatReport', { params })
      if (res.data?.success) setVatResults(res.data)
    } catch (e) {
      setVatResults({ data: [], total: 0, totalVat: 0, count: 0 })
    } finally { setVatLoading(false) }
  }

  const exportVatExcel = async () => {
    try {
      const params = { startDate: vatStart, endDate: vatEnd }
      if (financialBranchId && financialBranchId !== 'all') params.branchId = financialBranchId
      const res = await axiosInstance.get('exportVatReport', {
        params,
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `vat_report_${vatStart}_${vatEnd}.xlsx`)
      document.body.appendChild(link); link.click(); link.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) { console.error('VAT export failed', e) }
  }

  const openProviderModal = () => {
    setProviderResults(null)
    setProviderDoctorId('')
    setProviderStart('')
    setProviderEnd('')
    if (providerDoctors.length === 0) {
      axiosInstance.get('getAllDoctors').then(res => {
        if (res.data?.success) setProviderDoctors(res.data.data || [])
      }).catch(() => {})
    }
    setShowModal(false)
    setProviderModal(true)
  }

  const runProviderReport = async () => {
    if (!providerDoctorId || !providerStart || !providerEnd) return
    setProviderLoading(true)
    setProviderResults(null)
    try {
      const params = { doctorId: providerDoctorId, startDate: providerStart, endDate: providerEnd }
      if (financialBranchId && financialBranchId !== 'all') params.branchId = financialBranchId
      const res = await axiosInstance.get('getServicesByProvider', { params })
      if (res.data?.success) setProviderResults(res.data)
    } catch (e) {
      setProviderResults({ data: [], total: 0, totalVat: 0, count: 0 })
    } finally {
      setProviderLoading(false)
    }
  }

  const exportProviderExcel = async () => {
    try {
      const params = { doctorId: providerDoctorId, startDate: providerStart, endDate: providerEnd }
      if (financialBranchId && financialBranchId !== 'all') params.branchId = financialBranchId
      const res = await axiosInstance.get('exportServicesByProvider', {
        params,
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `services_by_provider_${providerStart}_${providerEnd}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      console.error('Export failed', e)
    }
  }

  const openServicesModal = () => {
    setSvcResults(null); setSvcStart(''); setSvcEnd('')
    setSvcServiceIds([]); setSvcStatus('all'); setSvcDoctorId('all')
    const fetches = []
    if (servicesList.length === 0)
      fetches.push(axiosInstance.get('getServices').then(res => { if (res.data?.data) setServicesList(res.data.data) }).catch(() => {}))
    if (servicesDoctors.length === 0)
      fetches.push(axiosInstance.get('getAllDoctors').then(res => { if (res.data?.success) setServicesDoctors(res.data.data || []) }).catch(() => {}))
    setShowModal(false)
    setServicesModal(true)
  }

  const runServicesReport = async () => {
    if (!svcStart || !svcEnd) return
    setSvcLoading(true); setSvcResults(null)
    try {
      const params = { startDate: svcStart, endDate: svcEnd }
      if (svcServiceIds.length > 0) params.serviceIds = svcServiceIds.map(s => s.value).join(',')
      if (svcStatus !== 'all') params.status = svcStatus
      if (svcDoctorId !== 'all') params.doctorId = svcDoctorId
      if (financialBranchId && financialBranchId !== 'all') params.branchId = financialBranchId
      const res = await axiosInstance.get('getServicesReport', { params })
      if (res.data?.success) setSvcResults(res.data)
    } catch (e) {
      setSvcResults({ data: [], total: 0, totalAmount: 0, totalVat: 0, count: 0 })
    } finally { setSvcLoading(false) }
  }

  const exportServicesExcel = async () => {
    try {
      const params = { startDate: svcStart, endDate: svcEnd }
      if (svcServiceIds.length > 0) params.serviceIds = svcServiceIds.map(s => s.value).join(',')
      if (svcStatus !== 'all') params.status = svcStatus
      if (svcDoctorId !== 'all') params.doctorId = svcDoctorId
      if (financialBranchId && financialBranchId !== 'all') params.branchId = financialBranchId
      const res = await axiosInstance.get('exportServicesReport', { params, responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `services_report_${svcStart}_${svcEnd}.xlsx`)
      document.body.appendChild(link); link.click(); link.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) { console.error('Services export failed', e) }
  }

  const openLabsByDoctorModal = () => {
    setLabDocResults(null); setLabDocStart(''); setLabDocEnd('')
    setLabDocDoctorId('all'); setLabDocServiceIds([]); setLabDocStatus('all')
    const bid = activeBranchId || parseInt(localStorage.getItem('activeBranchId')) || null
    setFinancialBranchId(bid ? String(bid) : 'all')
    if (labDocDoctors.length === 0) {
      axiosInstance.get('getAllDoctors').then(res => {
        if (res.data?.success) setLabDocDoctors(res.data.data || [])
      }).catch(() => {})
    }
    if (labDocServices.length === 0) {
      axiosInstance.get('getAllLabServices').then(res => {
        if (res.data?.success) setLabDocServices(res.data.data || [])
      }).catch(() => {})
    }
    setShowModal(false)
    setLabDocModal(true)
  }

  const labsByDoctorParams = () => {
    const params = { startDate: labDocStart, endDate: labDocEnd }
    if (labDocDoctorId !== 'all') params.doctorId = labDocDoctorId
    if (labDocStatus !== 'all') params.status = labDocStatus
    if (labDocServiceIds.length > 0) params.labServiceIds = labDocServiceIds.map(s => s.value).join(',')
    if (financialBranchId && financialBranchId !== 'all') params.branchId = financialBranchId
    return params
  }

  const runLabsByDoctorReport = async () => {
    if (!labDocStart || !labDocEnd) return
    setLabDocLoading(true); setLabDocResults(null)
    try {
      const res = await axiosInstance.get('getLabsByDoctorReport', { params: labsByDoctorParams() })
      if (res.data?.success) setLabDocResults(res.data)
    } catch (e) {
      setLabDocResults({ data: [], total: 0, totalPrice: 0, totalDiscount: 0, count: 0 })
    } finally { setLabDocLoading(false) }
  }

  const exportLabsByDoctorExcel = async () => {
    try {
      const res = await axiosInstance.get('exportLabsByDoctorReport', { params: labsByDoctorParams(), responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `labs_by_doctor_${labDocStart}_${labDocEnd}.xlsx`)
      document.body.appendChild(link); link.click(); link.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) { console.error('Labs by Doctor export failed', e) }
  }

  const openIncomeModal = () => {
    setIncomeResults(null); setIncomeStart(''); setIncomeEnd('')
    setIncomeMethod('all'); setIncomeStatus('all'); setIncomeDoctorId('all')
    if (incomeDoctors.length === 0) {
      axiosInstance.get('getAllDoctors').then(res => {
        if (res.data?.success) setIncomeDoctors(res.data.data || [])
      }).catch(() => {})
    }
    setShowModal(false)
    setIncomeModal(true)
  }

  const runIncomeReport = async () => {
    if (!incomeStart || !incomeEnd) return
    setIncomeLoading(true); setIncomeResults(null)
    try {
      const params = { startDate: incomeStart, endDate: incomeEnd }
      if (incomeMethod !== 'all') params.paymentMethod = incomeMethod
      if (incomeStatus !== 'all') params.paymentStatus = incomeStatus
      if (incomeDoctorId !== 'all') params.doctorId = incomeDoctorId
      if (financialBranchId && financialBranchId !== 'all') params.branchId = financialBranchId
      const res = await axiosInstance.get('getIncomeReport', { params })
      if (res.data?.success) setIncomeResults(res.data)
    } catch (e) {
      setIncomeResults({ data: [], total: 0, count: 0, byMethod: {} })
    } finally { setIncomeLoading(false) }
  }

  const exportIncomeExcel = async () => {
    try {
      const params = { startDate: incomeStart, endDate: incomeEnd }
      if (incomeMethod !== 'all') params.paymentMethod = incomeMethod
      if (incomeStatus !== 'all') params.paymentStatus = incomeStatus
      if (incomeDoctorId !== 'all') params.doctorId = incomeDoctorId
      if (financialBranchId && financialBranchId !== 'all') params.branchId = financialBranchId
      const res = await axiosInstance.get('exportIncomeReport', { params, responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `income_report_${incomeStart}_${incomeEnd}.xlsx`)
      document.body.appendChild(link); link.click(); link.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) { console.error('Income export failed', e) }
  }

  const openInvoicesModal = () => {
    setInvoicesResults(null); setInvoicesStart(''); setInvoicesEnd('')
    setInvoicesStatus('all'); setInvoicesRecordStatus('all'); setInvoicesUserId('all')
    if (invoicesUsers.length === 0) {
      axiosInstance.get('getAllUsersForView').then(res => {
        if (res.data?.success) setInvoicesUsers(res.data.data || [])
      }).catch(() => {})
    }
    setShowModal(false)
    setInvoicesModal(true)
  }

  const runInvoicesReport = async () => {
    if (!invoicesStart || !invoicesEnd) return
    setInvoicesLoading(true); setInvoicesResults(null)
    try {
      const params = { startDate: invoicesStart, endDate: invoicesEnd }
      if (invoicesStatus !== 'all') params.invoiceStatus = invoicesStatus
      if (invoicesRecordStatus !== 'all') params.status = invoicesRecordStatus
      if (invoicesUserId !== 'all') params.userId = invoicesUserId
      if (financialBranchId && financialBranchId !== 'all') params.branchId = financialBranchId
      const res = await axiosInstance.get('getInvoicesReport', { params })
      if (res.data?.success) setInvoicesResults(res.data)
    } catch (e) {
      setInvoicesResults({ data: [], count: 0, totalAmount: 0, totalVat: 0, totalPaid: 0, totalRemaining: 0 })
    } finally { setInvoicesLoading(false) }
  }

  const exportInvoicesExcel = async () => {
    try {
      const params = { startDate: invoicesStart, endDate: invoicesEnd }
      if (invoicesStatus !== 'all') params.invoiceStatus = invoicesStatus
      if (invoicesRecordStatus !== 'all') params.status = invoicesRecordStatus
      if (invoicesUserId !== 'all') params.userId = invoicesUserId
      if (financialBranchId && financialBranchId !== 'all') params.branchId = financialBranchId
      const res = await axiosInstance.get('exportInvoicesReport', { params, responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `invoices_report_${invoicesStart}_${invoicesEnd}.xlsx`)
      document.body.appendChild(link); link.click(); link.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) { console.error('Invoices export failed', e) }
  }

  const handleDownload = async key => {
    const config = reportConfig[key]
    if (!config) return
    setDownloading(key)
    try {
      const params = { ...(config.extraParams || {}) }
      if (selectedCategory?.key === 'financial') {
        if (startDate) params.startDate = startDate
        if (endDate) params.endDate = endDate
        if (financialBranchId && financialBranchId !== 'all') params.branchId = financialBranchId
      }
      const response = await axiosInstance.get(config.endpoint, { params, responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', config.filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      // If the server returned an error blob, decode it for a readable message
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text().catch(() => '')
        try {
          const json = JSON.parse(text)
          console.error('Download failed:', json.error || json.message || text)
        } catch {
          console.error('Download failed:', text || err.message)
        }
      } else {
        console.error('Download failed:', err.message || err)
      }
    } finally {
      setDownloading(null)
    }
  }

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: `${COLORS.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ti ti-chart-bar" style={{ color: COLORS.blue, fontSize: 19 }} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1a202c' }}>Reports</div>
              <div style={{ fontSize: 11, color: COLORS.gray }}>Download and manage all reports</div>
            </div>
          </div>

          <div style={{ padding: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
              {categories.map(cat => {
                const hasPermission = permissions[PERM_KEY[cat.key]] ?? false
                return (
                  <NavCard
                    key={cat.key}
                    icon={cat.icon}
                    title={cat.title}
                    disabled={!hasPermission}
                    onClick={() => handleCardClick(cat)}
                  />
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {showModal && selectedCategory && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 60, overflowY: 'auto' }} onClick={closeModal}>
          <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 500, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', marginBottom: 40 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 24px', borderBottom: '1px solid #e8ecf0' }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: `${COLORS.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className={`ti ${selectedCategory.icon}`} style={{ color: COLORS.blue, fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>{selectedCategory.title}</div>
                <div style={{ fontSize: 11, color: COLORS.gray }}>Select a report to download</div>
              </div>
              <button onClick={closeModal} style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14 }}>
                <i className="ti ti-x" />
              </button>
            </div>

            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {selectedCategory.key === 'financial' && (
                <div style={{ display: 'flex', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Start Date</label>
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none' }} onFocus={e => e.target.style.borderColor = COLORS.blue} onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                  </div>
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>End Date</label>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none' }} onFocus={e => e.target.style.borderColor = COLORS.blue} onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                  </div>
                  {allBranchSettings && allBranchSettings.length > 1 && (
                    <div style={{ flex: 1, minWidth: 140 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Branch</label>
                      <select value={financialBranchId} onChange={e => setFinancialBranchId(e.target.value)} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}>
                        <option value="all">All Branches</option>
                        {allBranchSettings.map((b, i) => (
                          <option key={b.id} value={String(b.id)}>Branch {i + 1}{b.official_name ? ` — ${b.official_name}` : ''}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {selectedCategory.items.map(item => {
                const isDownloading = downloading === item.key
                const isSpecial = item.special
                const hasConfig = isSpecial || !!reportConfig[item.key]
                return (
                  <button
                    key={item.key}
                    onClick={() => isSpecial ? (item.specialType === 'vat' ? openVatModal() : item.specialType === 'income' ? openIncomeModal() : item.specialType === 'services' ? openServicesModal() : item.specialType === 'invoices' ? openInvoicesModal() : item.specialType === 'labsByDoctor' ? openLabsByDoctorModal() : openProviderModal()) : (hasConfig && handleDownload(item.key))}
                    disabled={isDownloading || !hasConfig}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '11px 14px', borderRadius: 8,
                      border: `1px solid ${isSpecial ? `${COLORS.green}44` : '#e8ecf0'}`,
                      background: isSpecial ? `${COLORS.green}08` : isDownloading ? `${COLORS.blue}10` : '#fafbfc',
                      cursor: hasConfig ? 'pointer' : 'not-allowed',
                      color: hasConfig ? '#1a202c' : '#aaa', fontSize: 13, fontWeight: 500,
                      transition: 'background 0.15s, border-color 0.15s',
                    }}
                    onMouseEnter={e => { if (hasConfig && !isDownloading) { e.currentTarget.style.background = isSpecial ? `${COLORS.green}18` : `${COLORS.blue}12`; e.currentTarget.style.borderColor = isSpecial ? COLORS.green : COLORS.blue } }}
                    onMouseLeave={e => { if (!isDownloading) { e.currentTarget.style.background = isSpecial ? `${COLORS.green}08` : '#fafbfc'; e.currentTarget.style.borderColor = isSpecial ? `${COLORS.green}44` : '#e8ecf0' } }}
                  >
                    <span>{item.label}</span>
                    <i className={`ti ${isDownloading ? 'ti-loader-2' : isSpecial ? 'ti-stethoscope' : hasConfig ? 'ti-download' : 'ti-lock'}`} style={{ fontSize: 16, color: isDownloading ? COLORS.blue : isSpecial ? COLORS.green : hasConfig ? COLORS.green : '#ccc' }} />
                  </button>
                )
              })}
            </div>

            <div style={{ padding: '12px 24px', borderTop: '1px solid #e8ecf0', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={closeModal} style={{ padding: '7px 18px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── VAT Report Modal ── */}
      {vatModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 40, overflowY: 'auto' }} onClick={() => setVatModal(false)}>
          <div style={{ background: '#fff', borderRadius: 12, width: '98%', maxWidth: 1400, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', marginBottom: 40 }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 24px', borderBottom: '1px solid #e8ecf0' }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: `${COLORS.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="ti ti-receipt-tax" style={{ color: COLORS.blue, fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>VAT Report</div>
                <div style={{ fontSize: 11, color: COLORS.gray }}>Completed services with VAT breakdown filtered by date</div>
              </div>
              <button onClick={() => setVatModal(false)} style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14 }}>
                <i className="ti ti-x" />
              </button>
            </div>

            {/* Filters */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f2f5', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 140px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Start Date</label>
                <input type="date" value={vatStart} onChange={e => { setVatStart(e.target.value); setVatResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none' }} />
              </div>
              <div style={{ flex: '1 1 140px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>End Date</label>
                <input type="date" value={vatEnd} onChange={e => { setVatEnd(e.target.value); setVatResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none' }} />
              </div>
              {allBranchSettings && allBranchSettings.length > 1 && (
                <div style={{ flex: '1 1 160px' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Branch</label>
                  <select value={financialBranchId} onChange={e => { setFinancialBranchId(e.target.value); setVatResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}>
                    <option value="all">All Branches</option>
                    {allBranchSettings.map((b, i) => (
                      <option key={b.id} value={String(b.id)}>Branch {i + 1}{b.official_name ? ` — ${b.official_name}` : ''}</option>
                    ))}
                  </select>
                </div>
              )}
              <button
                onClick={runVatReport}
                disabled={!vatStart || !vatEnd || vatLoading}
                style={{ height: 36, padding: '0 20px', borderRadius: 8, border: 'none', background: (!vatStart || !vatEnd) ? '#e8ecf0' : COLORS.blue, color: (!vatStart || !vatEnd) ? '#aaa' : '#fff', fontSize: 13, fontWeight: 600, cursor: (!vatStart || !vatEnd) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
              >
                <i className={`ti ${vatLoading ? 'ti-loader-2' : 'ti-search'}`} />
                {vatLoading ? 'Loading…' : 'Generate'}
              </button>
            </div>

            {/* Results */}
            <div style={{ padding: '0 24px 24px' }}>
              {vatLoading && <div style={{ textAlign: 'center', padding: '32px 0', color: COLORS.gray, fontSize: 13 }}>Fetching VAT data…</div>}

              {!vatLoading && vatResults && (
                <>
                  {/* Summary */}
                  <div style={{ display: 'flex', gap: 12, padding: '14px 0 10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ background: `${COLORS.green}12`, border: `1px solid ${COLORS.green}33`, borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Services: </span>
                      <span style={{ fontWeight: 700, color: COLORS.green }}>{vatResults.count}</span>
                    </div>
                    <div style={{ background: `${COLORS.blue}12`, border: `1px solid ${COLORS.blue}44`, borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Total VAT: </span>
                      <span style={{ fontWeight: 700, color: '#4680ff' }}>{Number(vatResults.totalVat || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ background: `${COLORS.green}12`, border: `1px solid ${COLORS.green}44`, borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Net Total: </span>
                      <span style={{ fontWeight: 700, color: COLORS.green }}>{Number(vatResults.total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {vatResults.data.length > 0 && (
                      <button onClick={exportVatExcel} style={{ marginLeft: 'auto', height: 32, padding: '0 14px', borderRadius: 8, border: `1px solid ${COLORS.green}44`, background: `${COLORS.green}12`, color: COLORS.green, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i className="ti ti-file-spreadsheet" style={{ fontSize: 15 }} />Export Excel
                      </button>
                    )}
                  </div>

                  {vatResults.data.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '28px 0', color: '#bbb', fontSize: 13 }}>No completed services with VAT found in the selected period.</div>
                  ) : (
                    <div style={{ borderRadius: 8, border: '1px solid #e8ecf0' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: 32 }} />
                          <col style={{ width: 88 }} />
                          <col style={{ width: '16%' }} />
                          <col style={{ width: '14%' }} />
                          <col />
                          <col style={{ width: 80 }} />
                          <col style={{ width: 88 }} />
                          <col style={{ width: 64 }} />
                          <col style={{ width: 96 }} />
                          <col style={{ width: 88 }} />
                        </colgroup>
                        <thead>
                          <tr style={{ background: '#f8f9fb' }}>
                            {[
                              { h: '#', align: 'left' }, { h: 'Date', align: 'left' }, { h: 'Patient', align: 'left' },
                              { h: 'Doctor', align: 'left' }, { h: 'Service', align: 'left' }, { h: 'Code', align: 'left' },
                              { h: 'Amount', align: 'right' }, { h: 'VAT %', align: 'right' }, { h: 'VAT Amount', align: 'right' }, { h: 'Net', align: 'right' },
                            ].map(({ h, align }) => (
                              <th key={h} style={{ padding: '8px 8px', textAlign: align, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10, borderBottom: '1px solid #e8ecf0', whiteSpace: 'nowrap', overflow: 'hidden' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {vatResults.data.map((row, i) => (
                            <tr key={row.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                              <td style={{ padding: '7px 8px', color: '#9aa0ac', borderBottom: '1px solid #f0f2f5' }}>{i + 1}</td>
                              <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', whiteSpace: 'nowrap' }}>{row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-GB') : '—'}</td>
                              <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', wordBreak: 'break-word' }}>{row.patientName || '—'}</td>
                              <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', wordBreak: 'break-word' }}>{row.doctorName || '—'}</td>
                              <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', wordBreak: 'break-word' }}>{row.serviceName || '—'}</td>
                              <td style={{ padding: '7px 8px', color: '#5B6B79', borderBottom: '1px solid #f0f2f5', whiteSpace: 'nowrap' }}>{row.serviceCode || '—'}</td>
                              <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(row.amount || 0).toFixed(2)}</td>
                              <td style={{ padding: '7px 8px', color: '#4680ff', borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>{row.vat_percent ? `${row.vat_percent}%` : '0%'}</td>
                              <td style={{ padding: '7px 8px', color: '#4680ff', fontWeight: 600, borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(row.vat_amount || 0).toFixed(2)}</td>
                              <td style={{ padding: '7px 8px', fontWeight: 600, color: COLORS.green, borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(row.net_amount || 0).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: '#f0faf6' }}>
                            <td colSpan={8} style={{ padding: '8px 10px', fontWeight: 700, color: COLORS.gray, fontSize: 11, textTransform: 'uppercase' }}>Total</td>
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: '#4680ff', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(vatResults.totalVat || 0).toFixed(2)}</td>
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: COLORS.green, textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(vatResults.total).toFixed(2)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── By Provider Modal ── */}
      {providerModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 40, overflowY: 'auto' }} onClick={() => setProviderModal(false)}>
          <div style={{ background: '#fff', borderRadius: 12, width: '96%', maxWidth: 1100, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', marginBottom: 40 }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 24px', borderBottom: '1px solid #e8ecf0' }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: `${COLORS.green}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="ti ti-stethoscope" style={{ color: COLORS.green, fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Services by Provider</div>
                <div style={{ fontSize: 11, color: COLORS.gray }}>Completed services filtered by doctor and period</div>
              </div>
              <button onClick={() => setProviderModal(false)} style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14 }}>
                <i className="ti ti-x" />
              </button>
            </div>

            {/* Filters */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f2f5', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 180px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Doctor / Provider</label>
                <select
                  value={providerDoctorId}
                  onChange={e => { setProviderDoctorId(e.target.value); setProviderResults(null) }}
                  style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}
                >
                  <option value="">Select doctor…</option>
                  {providerDoctors.map(d => (
                    <option key={d.id} value={d.id}>
                      {[d.prefix, d.fullName, d.shortName].filter(Boolean).join(' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: '1 1 130px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Start Date</label>
                <input type="date" value={providerStart} onChange={e => { setProviderStart(e.target.value); setProviderResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none' }} />
              </div>
              <div style={{ flex: '1 1 130px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>End Date</label>
                <input type="date" value={providerEnd} onChange={e => { setProviderEnd(e.target.value); setProviderResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none' }} />
              </div>
              {allBranchSettings && allBranchSettings.length > 1 && (
                <div style={{ flex: '1 1 160px' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Branch</label>
                  <select value={financialBranchId} onChange={e => { setFinancialBranchId(e.target.value); setProviderResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}>
                    <option value="all">All Branches</option>
                    {allBranchSettings.map((b, i) => (
                      <option key={b.id} value={String(b.id)}>Branch {i + 1}{b.official_name ? ` — ${b.official_name}` : ''}</option>
                    ))}
                  </select>
                </div>
              )}
              <button
                onClick={runProviderReport}
                disabled={!providerDoctorId || !providerStart || !providerEnd || providerLoading}
                style={{ height: 36, padding: '0 20px', borderRadius: 8, border: 'none', background: (!providerDoctorId || !providerStart || !providerEnd) ? '#e8ecf0' : COLORS.green, color: (!providerDoctorId || !providerStart || !providerEnd) ? '#aaa' : '#fff', fontSize: 13, fontWeight: 600, cursor: (!providerDoctorId || !providerStart || !providerEnd) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
              >
                <i className={`ti ${providerLoading ? 'ti-loader-2' : 'ti-search'}`} />
                {providerLoading ? 'Loading…' : 'Generate'}
              </button>
            </div>

            {/* Results */}
            <div style={{ padding: '0 24px 24px' }}>
              {providerLoading && (
                <div style={{ textAlign: 'center', padding: '32px 0', color: COLORS.gray, fontSize: 13 }}>Fetching services…</div>
              )}

              {!providerLoading && providerResults && (
                <>
                  {/* Summary bar + Export */}
                  <div style={{ display: 'flex', gap: 12, padding: '14px 0 10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ background: `${COLORS.green}12`, border: `1px solid ${COLORS.green}33`, borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Services: </span>
                      <span style={{ fontWeight: 700, color: COLORS.green }}>{providerResults.count}</span>
                    </div>
                    <div style={{ background: `${COLORS.blue}12`, border: `1px solid ${COLORS.blue}44`, borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>VAT: </span>
                      <span style={{ fontWeight: 700, color: '#1a202c' }}>
                        {Number(providerResults.totalVat || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div style={{ background: `${COLORS.green}12`, border: `1px solid ${COLORS.green}44`, borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Net Total: </span>
                      <span style={{ fontWeight: 700, color: COLORS.green }}>
                        {Number(providerResults.total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    {providerResults.data.length > 0 && (
                      <button
                        onClick={exportProviderExcel}
                        style={{ marginLeft: 'auto', height: 32, padding: '0 14px', borderRadius: 8, border: `1px solid ${COLORS.green}44`, background: `${COLORS.green}12`, color: COLORS.green, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        <i className="ti ti-file-spreadsheet" style={{ fontSize: 15 }} />Export Excel
                      </button>
                    )}
                  </div>

                  {providerResults.data.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '28px 0', color: '#bbb', fontSize: 13 }}>No completed services found for this provider in the selected period.</div>
                  ) : (
                    <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e8ecf0' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: '#f8f9fb' }}>
                            {['#', 'Date', 'Patient', 'Service', 'Code', 'Amount', 'VAT', 'Discount', 'Net'].map(h => (
                              <th key={h} style={{ padding: '8px 10px', textAlign: h === 'Amount' || h === 'VAT' || h === 'Discount' || h === 'Net' ? 'right' : 'left', fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10, borderBottom: '1px solid #e8ecf0', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {providerResults.data.map((row, i) => (
                            <tr key={row.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                              <td style={{ padding: '7px 10px', color: '#9aa0ac', borderBottom: '1px solid #f0f2f5' }}>{i + 1}</td>
                              <td style={{ padding: '7px 10px', color: '#374151', borderBottom: '1px solid #f0f2f5', whiteSpace: 'nowrap' }}>
                                {row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-GB') : '—'}
                              </td>
                              <td style={{ padding: '7px 10px', color: '#374151', borderBottom: '1px solid #f0f2f5' }}>{row.patientName || '—'}</td>
                              <td style={{ padding: '7px 10px', color: '#374151', borderBottom: '1px solid #f0f2f5' }}>{row.serviceName || '—'}</td>
                              <td style={{ padding: '7px 10px', color: '#5B6B79', borderBottom: '1px solid #f0f2f5', whiteSpace: 'nowrap' }}>{row.serviceCode || '—'}</td>
                              <td style={{ padding: '7px 10px', color: '#374151', borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(row.amount || 0).toFixed(2)}</td>
                              <td style={{ padding: '7px 10px', color: '#4680ff', borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(row.vat_value || 0).toFixed(2)}</td>
                              <td style={{ padding: '7px 10px', color: '#9E2B25', borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {row.discount_value ? `${row.discount_value}${row.discount_type === 'percentage' ? '%' : ''}` : '—'}
                              </td>
                              <td style={{ padding: '7px 10px', fontWeight: 600, color: COLORS.green, borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(row.net_amount || 0).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: '#f0faf6' }}>
                            <td colSpan={6} style={{ padding: '8px 10px', fontWeight: 700, color: COLORS.gray, fontSize: 11, textTransform: 'uppercase' }}>Total</td>
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: '#4680ff', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(providerResults.totalVat || 0).toFixed(2)}</td>
                            <td style={{ padding: '8px 10px', borderBottom: 'none' }} />
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: COLORS.green, textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(providerResults.total).toFixed(2)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ── Services Report Modal ── */}
      {servicesModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 40, overflowY: 'auto' }} onClick={() => setServicesModal(false)}>
          <div style={{ background: '#fff', borderRadius: 12, width: '98%', maxWidth: 1400, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', marginBottom: 40 }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 24px', borderBottom: '1px solid #e8ecf0' }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: '#7c3aed20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="ti ti-clipboard-list" style={{ color: '#7c3aed', fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Services Report</div>
                <div style={{ fontSize: 11, color: COLORS.gray }}>Patient services from patient_services — filter by service, status, provider and date</div>
              </div>
              <button onClick={() => setServicesModal(false)} style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14 }}>
                <i className="ti ti-x" />
              </button>
            </div>

            {/* Filters */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f2f5', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 130px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Start Date</label>
                <input type="date" value={svcStart} onChange={e => { setSvcStart(e.target.value); setSvcResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none' }} />
              </div>
              <div style={{ flex: '1 1 130px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>End Date</label>
                <input type="date" value={svcEnd} onChange={e => { setSvcEnd(e.target.value); setSvcResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none' }} />
              </div>
              <div style={{ flex: '3 1 280px', minWidth: 0 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>
                  Services {svcServiceIds.length > 0 && <span style={{ color: '#7c3aed', fontWeight: 700 }}>({svcServiceIds.length} selected)</span>}
                </label>
                <Select
                  isMulti
                  isClearable
                  isSearchable
                  placeholder="All services — type to search…"
                  options={servicesList.map(s => ({ value: s.id, label: s.code ? `[${s.code}] ${s.name}` : s.name }))}
                  value={svcServiceIds}
                  onChange={selected => { setSvcServiceIds(selected || []); setSvcResults(null) }}
                  styles={{
                    control: base => ({ ...base, minHeight: 36, borderRadius: 8, borderColor: '#dde3eb', fontSize: 13, boxShadow: 'none', '&:hover': { borderColor: '#7c3aed' } }),
                    valueContainer: base => ({ ...base, padding: '2px 8px', flexWrap: 'wrap' }),
                    multiValue: base => ({ ...base, background: '#f5f0ff', borderRadius: 6 }),
                    multiValueLabel: base => ({ ...base, color: '#7c3aed', fontSize: 12, fontWeight: 600 }),
                    multiValueRemove: base => ({ ...base, color: '#7c3aed', ':hover': { background: '#7c3aed', color: '#fff' } }),
                    option: (base, { isFocused }) => ({ ...base, fontSize: 13, background: isFocused ? '#f5f0ff' : '#fff', color: '#1a202c' }),
                    menu: base => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
              <div style={{ flex: '1 1 130px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Status</label>
                <select value={svcStatus} onChange={e => { setSvcStatus(e.target.value); setSvcResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}>
                  <option value="all">All Statuses</option>
                  <option value="complete">Complete</option>
                  <option value="pending">Pending</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div style={{ flex: '1 1 170px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Provider</label>
                <select value={svcDoctorId} onChange={e => { setSvcDoctorId(e.target.value); setSvcResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}>
                  <option value="all">All Providers</option>
                  {servicesDoctors.map(d => (
                    <option key={d.id} value={d.id}>{[d.prefix, d.fullName, d.shortName].filter(Boolean).join(' ')}</option>
                  ))}
                </select>
              </div>
              {allBranchSettings && allBranchSettings.length > 1 && (
                <div style={{ flex: '1 1 160px' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Branch</label>
                  <select value={financialBranchId} onChange={e => { setFinancialBranchId(e.target.value); setSvcResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}>
                    <option value="all">All Branches</option>
                    {allBranchSettings.map((b, i) => (
                      <option key={b.id} value={String(b.id)}>Branch {i + 1}{b.official_name ? ` — ${b.official_name}` : ''}</option>
                    ))}
                  </select>
                </div>
              )}
              <button
                onClick={runServicesReport}
                disabled={!svcStart || !svcEnd || svcLoading}
                style={{ height: 36, padding: '0 20px', borderRadius: 8, border: 'none', background: (!svcStart || !svcEnd) ? '#e8ecf0' : '#7c3aed', color: (!svcStart || !svcEnd) ? '#aaa' : '#fff', fontSize: 13, fontWeight: 600, cursor: (!svcStart || !svcEnd) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
              >
                <i className={`ti ${svcLoading ? 'ti-loader-2' : 'ti-search'}`} />
                {svcLoading ? 'Loading…' : 'Generate'}
              </button>
            </div>

            {/* Results */}
            <div style={{ padding: '0 24px 24px' }}>
              {svcLoading && <div style={{ textAlign: 'center', padding: '32px 0', color: COLORS.gray, fontSize: 13 }}>Fetching services…</div>}

              {!svcLoading && svcResults && (
                <>
                  {/* Summary */}
                  <div style={{ display: 'flex', gap: 12, padding: '14px 0 10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ background: '#7c3aed12', border: '1px solid #7c3aed33', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Services: </span>
                      <span style={{ fontWeight: 700, color: '#7c3aed' }}>{svcResults.count}</span>
                    </div>
                    <div style={{ background: '#f8f9fb', border: '1px solid #e8ecf0', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Gross Amount: </span>
                      <span style={{ fontWeight: 700, color: '#374151' }}>{Number(svcResults.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ background: '#4680ff12', border: '1px solid #4680ff33', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Total VAT: </span>
                      <span style={{ fontWeight: 700, color: '#4680ff' }}>{Number(svcResults.totalVat || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ background: `${COLORS.green}12`, border: `1px solid ${COLORS.green}44`, borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Net Total: </span>
                      <span style={{ fontWeight: 700, color: COLORS.green }}>{Number(svcResults.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {svcResults.data.length > 0 && (
                      <button onClick={exportServicesExcel} style={{ marginLeft: 'auto', height: 32, padding: '0 14px', borderRadius: 8, border: '1px solid #7c3aed44', background: '#7c3aed12', color: '#7c3aed', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i className="ti ti-file-spreadsheet" style={{ fontSize: 15 }} />Export Excel
                      </button>
                    )}
                  </div>

                  {svcResults.data.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '28px 0', color: '#bbb', fontSize: 13 }}>No services found for the selected filters.</div>
                  ) : (
                    <div style={{ borderRadius: 8, border: '1px solid #e8ecf0' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: 32 }} />
                          <col style={{ width: 88 }} />
                          <col style={{ width: '15%' }} />
                          <col />
                          <col style={{ width: 76 }} />
                          <col style={{ width: 84 }} />
                          <col style={{ width: 80 }} />
                          <col style={{ width: 72 }} />
                          <col style={{ width: 84 }} />
                          <col style={{ width: 86 }} />
                          <col style={{ width: 86 }} />
                          <col style={{ width: '14%' }} />
                        </colgroup>
                        <thead>
                          <tr style={{ background: '#f5f0ff' }}>
                            {[
                              { h: '#', a: 'left' }, { h: 'Date', a: 'left' }, { h: 'Patient', a: 'left' },
                              { h: 'Service', a: 'left' }, { h: 'Code', a: 'left' },
                              { h: 'Amount', a: 'right' }, { h: 'Discount', a: 'right' },
                              { h: 'VAT', a: 'right' }, { h: 'Net', a: 'right' },
                              { h: 'Status', a: 'center' }, { h: 'Billing', a: 'center' }, { h: 'Provider', a: 'left' },
                            ].map(({ h, a }) => (
                              <th key={h} style={{ padding: '8px 8px', textAlign: a, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10, borderBottom: '1px solid #ddd0f8', whiteSpace: 'nowrap', overflow: 'hidden' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {svcResults.data.map((row, i) => {
                            const isComplete = row.status?.toLowerCase() === 'complete'
                            const isPending = row.status?.toLowerCase() === 'pending'
                            return (
                              <tr key={row.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                <td style={{ padding: '7px 8px', color: '#9aa0ac', borderBottom: '1px solid #f0f2f5' }}>{i + 1}</td>
                                <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', whiteSpace: 'nowrap' }}>{row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-GB') : '—'}</td>
                                <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', wordBreak: 'break-word' }}>{row.patientName || '—'}</td>
                                <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', wordBreak: 'break-word' }}>{row.serviceName || '—'}</td>
                                <td style={{ padding: '7px 8px', color: '#5B6B79', borderBottom: '1px solid #f0f2f5', whiteSpace: 'nowrap' }}>{row.serviceCode || '—'}</td>
                                <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(row.amount || 0).toFixed(2)}</td>
                                <td style={{ padding: '7px 8px', color: '#b45309', borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                  {row.discount_value ? `${row.discount_value}${row.discount_type === 'percentage' ? '%' : ''}` : '—'}
                                </td>
                                <td style={{ padding: '7px 8px', color: '#4680ff', borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(row.vat_value || 0).toFixed(2)}</td>
                                <td style={{ padding: '7px 8px', fontWeight: 600, color: COLORS.green, borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(row.net_amount || 0).toFixed(2)}</td>
                                <td style={{ padding: '7px 8px', borderBottom: '1px solid #f0f2f5', textAlign: 'center' }}>
                                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: isComplete ? '#e8faf3' : isPending ? '#fff8e1' : '#fff0f0', color: isComplete ? COLORS.green : isPending ? '#b45309' : COLORS.red, textTransform: 'capitalize' }}>
                                    {row.status || '—'}
                                  </span>
                                </td>
                                <td style={{ padding: '7px 8px', borderBottom: '1px solid #f0f2f5', textAlign: 'center' }}>
                                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: row.billing_status === 'Billed' ? '#e8eeff' : '#f8f9fb', color: row.billing_status === 'Billed' ? '#4680ff' : '#5B6B79' }}>
                                    {row.billing_status || '—'}
                                  </span>
                                </td>
                                <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', wordBreak: 'break-word' }}>{row.doctorName || '—'}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: '#f5f0ff' }}>
                            <td colSpan={5} style={{ padding: '8px 10px', fontWeight: 700, color: COLORS.gray, fontSize: 11, textTransform: 'uppercase' }}>Total ({svcResults.count} services)</td>
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: '#374151', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(svcResults.totalAmount || 0).toFixed(2)}</td>
                            <td />
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: '#4680ff', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(svcResults.totalVat || 0).toFixed(2)}</td>
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: COLORS.green, textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(svcResults.total || 0).toFixed(2)}</td>
                            <td colSpan={3} />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Labs by Doctor Modal ── */}
      {labDocModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 40, overflowY: 'auto' }} onClick={() => setLabDocModal(false)}>
          <div style={{ background: '#fff', borderRadius: 12, width: '98%', maxWidth: 1400, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', marginBottom: 40 }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 24px', borderBottom: '1px solid #e8ecf0' }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: '#0e749020', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="ti ti-test-pipe" style={{ color: '#0e7490', fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Labs by Doctor</div>
                <div style={{ fontSize: 11, color: COLORS.gray }}>Lab requests by ordering doctor — filter by date, doctor and lab tests</div>
              </div>
              <button onClick={() => setLabDocModal(false)} style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14 }}>
                <i className="ti ti-x" />
              </button>
            </div>

            {/* Filters */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f2f5', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 130px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Start Date</label>
                <input type="date" value={labDocStart} onChange={e => { setLabDocStart(e.target.value); setLabDocResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none' }} />
              </div>
              <div style={{ flex: '1 1 130px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>End Date</label>
                <input type="date" value={labDocEnd} onChange={e => { setLabDocEnd(e.target.value); setLabDocResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none' }} />
              </div>
              <div style={{ flex: '1 1 180px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Doctor Name</label>
                <select value={labDocDoctorId} onChange={e => { setLabDocDoctorId(e.target.value); setLabDocResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}>
                  <option value="all">All Doctors</option>
                  {labDocDoctors.map(d => (
                    <option key={d.id} value={d.id}>{[d.prefix, d.fullName, d.shortName].filter(Boolean).join(' ')}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: '3 1 280px', minWidth: 0 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>
                  Lab Requests {labDocServiceIds.length > 0 && <span style={{ color: '#0e7490', fontWeight: 700 }}>({labDocServiceIds.length} selected)</span>}
                </label>
                <Select
                  isMulti
                  isClearable
                  isSearchable
                  placeholder="All lab requests — type to search…"
                  options={labDocServices.map(s => ({ value: s.id, label: s.category ? `${s.serviceName} — ${s.category}` : s.serviceName }))}
                  value={labDocServiceIds}
                  onChange={selected => { setLabDocServiceIds(selected || []); setLabDocResults(null) }}
                  styles={{
                    control: base => ({ ...base, minHeight: 36, borderRadius: 8, borderColor: '#dde3eb', fontSize: 13, boxShadow: 'none', '&:hover': { borderColor: '#0e7490' } }),
                    valueContainer: base => ({ ...base, padding: '2px 8px', flexWrap: 'wrap' }),
                    multiValue: base => ({ ...base, background: '#e0f5fa', borderRadius: 6 }),
                    multiValueLabel: base => ({ ...base, color: '#0e7490', fontSize: 12, fontWeight: 600 }),
                    multiValueRemove: base => ({ ...base, color: '#0e7490', ':hover': { background: '#0e7490', color: '#fff' } }),
                    option: (base, { isFocused }) => ({ ...base, fontSize: 13, background: isFocused ? '#e0f5fa' : '#fff', color: '#1a202c' }),
                    menu: base => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
              <div style={{ flex: '1 1 140px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Status</label>
                <select value={labDocStatus} onChange={e => { setLabDocStatus(e.target.value); setLabDocResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}>
                  <option value="all">All Statuses</option>
                  <option value="not sent">Not Sent</option>
                  <option value="pending">Pending</option>
                  <option value="received">Received</option>
                  <option value="result">Result</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              {allBranchSettings && allBranchSettings.length > 1 && (
                <div style={{ flex: '1 1 160px' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Branch</label>
                  <select value={financialBranchId} onChange={e => { setFinancialBranchId(e.target.value); setLabDocResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}>
                    <option value="all">All Branches</option>
                    {allBranchSettings.map((b, i) => (
                      <option key={b.id} value={String(b.id)}>Branch {i + 1}{b.official_name ? ` — ${b.official_name}` : ''}</option>
                    ))}
                  </select>
                </div>
              )}
              <button
                onClick={runLabsByDoctorReport}
                disabled={!labDocStart || !labDocEnd || labDocLoading}
                style={{ height: 36, padding: '0 20px', borderRadius: 8, border: 'none', background: (!labDocStart || !labDocEnd) ? '#e8ecf0' : '#0e7490', color: (!labDocStart || !labDocEnd) ? '#aaa' : '#fff', fontSize: 13, fontWeight: 600, cursor: (!labDocStart || !labDocEnd) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
              >
                <i className={`ti ${labDocLoading ? 'ti-loader-2' : 'ti-search'}`} />
                {labDocLoading ? 'Loading…' : 'Generate'}
              </button>
            </div>

            {/* Results */}
            <div style={{ padding: '0 24px 24px' }}>
              {labDocLoading && <div style={{ textAlign: 'center', padding: '32px 0', color: COLORS.gray, fontSize: 13 }}>Fetching lab requests…</div>}

              {!labDocLoading && labDocResults && (
                <>
                  {/* Summary */}
                  <div style={{ display: 'flex', gap: 12, padding: '14px 0 10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ background: '#0e749012', border: '1px solid #0e749033', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Lab Requests: </span>
                      <span style={{ fontWeight: 700, color: '#0e7490' }}>{labDocResults.count}</span>
                    </div>
                    <div style={{ background: '#f8f9fb', border: '1px solid #e8ecf0', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Gross Price: </span>
                      <span style={{ fontWeight: 700, color: '#374151' }}>{Number(labDocResults.totalPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ background: '#fff8e1', border: '1px solid #f0d9a0', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Total Discount: </span>
                      <span style={{ fontWeight: 700, color: '#b45309' }}>{Number(labDocResults.totalDiscount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ background: `${COLORS.green}12`, border: `1px solid ${COLORS.green}44`, borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Net Amount: </span>
                      <span style={{ fontWeight: 700, color: COLORS.green }}>{Number(labDocResults.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {labDocResults.data.length > 0 && (
                      <button onClick={exportLabsByDoctorExcel} style={{ marginLeft: 'auto', height: 32, padding: '0 14px', borderRadius: 8, border: '1px solid #0e749044', background: '#0e749012', color: '#0e7490', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i className="ti ti-file-spreadsheet" style={{ fontSize: 15 }} />Export Excel
                      </button>
                    )}
                  </div>

                  {labDocResults.data.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '28px 0', color: '#bbb', fontSize: 13 }}>No lab requests found for the selected filters.</div>
                  ) : (
                    <div style={{ borderRadius: 8, border: '1px solid #e8ecf0' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: 32 }} />
                          <col style={{ width: 88 }} />
                          <col style={{ width: 80 }} />
                          <col style={{ width: '14%' }} />
                          <col style={{ width: '13%' }} />
                          <col style={{ width: '11%' }} />
                          <col />
                          <col style={{ width: 82 }} />
                          <col style={{ width: 82 }} />
                          <col style={{ width: 90 }} />
                          <col style={{ width: 86 }} />
                          <col style={{ width: 86 }} />
                        </colgroup>
                        <thead>
                          <tr style={{ background: '#e8f7fb' }}>
                            {[
                              { h: '#', a: 'left' }, { h: 'Date', a: 'left' }, { h: 'File No', a: 'left' },
                              { h: 'Patient', a: 'left' }, { h: 'Doctor', a: 'left' }, { h: 'Lab', a: 'left' },
                              { h: 'Tests', a: 'left' },
                              { h: 'Price', a: 'right' }, { h: 'Discount', a: 'right' }, { h: 'Net Amount', a: 'right' },
                              { h: 'Status', a: 'center' }, { h: 'Billing', a: 'center' },
                            ].map(({ h, a }) => (
                              <th key={h} style={{ padding: '8px 8px', textAlign: a, fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10, borderBottom: '1px solid #c3e8f2', verticalAlign: 'bottom', ...WRAP }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {labDocResults.data.map((row, i) => {
                            const st = (row.status || '').toLowerCase()
                            const bg = st === 'result' || st === 'received' ? '#e8faf3' : st === 'pending' ? '#fff8e1' : st === 'cancelled' ? '#fff0f0' : '#f1f3f6'
                            const fg = st === 'result' || st === 'received' ? COLORS.green : st === 'pending' ? '#b45309' : st === 'cancelled' ? COLORS.red : '#5B6B79'
                            const billed = (row.billing_status || '').toLowerCase()
                            return (
                              <tr key={row.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc', verticalAlign: 'top' }}>
                                <td style={{ padding: '7px 8px', color: '#9aa0ac', borderBottom: '1px solid #f0f2f5' }}>{i + 1}</td>
                                <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', whiteSpace: 'nowrap' }}>{row.created_at ? new Date(row.created_at).toLocaleDateString('en-GB') : '—'}</td>
                                <td style={{ padding: '7px 8px', color: '#5B6B79', borderBottom: '1px solid #f0f2f5', ...WRAP }}>{row.fileNumber || '—'}</td>
                                <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', ...WRAP }}>{row.patientName || '—'}</td>
                                <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', ...WRAP }}>{row.doctorName || '—'}</td>
                                <td style={{ padding: '7px 8px', color: '#5B6B79', borderBottom: '1px solid #f0f2f5', ...WRAP }}>{row.labName || '—'}</td>
                                <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', ...WRAP }}>{row.testNames || row.title || '—'}</td>
                                <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(row.price || 0).toFixed(2)}</td>
                                <td style={{ padding: '7px 8px', color: '#b45309', borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(row.discount_amount || 0).toFixed(2)}</td>
                                <td style={{ padding: '7px 8px', fontWeight: 600, color: COLORS.green, borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(row.net_amount || 0).toFixed(2)}</td>
                                <td style={{ padding: '7px 8px', borderBottom: '1px solid #f0f2f5', textAlign: 'center' }}>
                                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: bg, color: fg, textTransform: 'capitalize' }}>
                                    {row.status || '—'}
                                  </span>
                                </td>
                                <td style={{ padding: '7px 8px', borderBottom: '1px solid #f0f2f5', textAlign: 'center' }}>
                                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: billed === 'paid' ? '#e8faf3' : billed === 'billed' ? '#e8eeff' : '#f8f9fb', color: billed === 'paid' ? COLORS.green : billed === 'billed' ? '#4680ff' : '#5B6B79', textTransform: 'capitalize' }}>
                                    {row.billing_status || '—'}
                                  </span>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: '#e8f7fb' }}>
                            <td colSpan={7} style={{ padding: '8px 10px', fontWeight: 700, color: COLORS.gray, fontSize: 11, textTransform: 'uppercase' }}>Total ({labDocResults.count} lab requests)</td>
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: '#374151', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(labDocResults.totalPrice || 0).toFixed(2)}</td>
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: '#b45309', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(labDocResults.totalDiscount || 0).toFixed(2)}</td>
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: COLORS.green, textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(labDocResults.total || 0).toFixed(2)}</td>
                            <td colSpan={2} />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Income Report Modal ── */}
      {incomeModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 40, overflowY: 'auto' }} onClick={() => setIncomeModal(false)}>
          <div style={{ background: '#fff', borderRadius: 12, width: '98%', maxWidth: 1400, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', marginBottom: 40 }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 24px', borderBottom: '1px solid #e8ecf0' }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: '#4680ff20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="ti ti-cash" style={{ color: '#4680ff', fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Income Report</div>
                <div style={{ fontSize: 11, color: COLORS.gray }}>All payments from patient_payment_invoices with flexible filtering</div>
              </div>
              <button onClick={() => setIncomeModal(false)} style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14 }}>
                <i className="ti ti-x" />
              </button>
            </div>

            {/* Filters */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f2f5', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 130px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Start Date</label>
                <input type="date" value={incomeStart} onChange={e => { setIncomeStart(e.target.value); setIncomeResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none' }} />
              </div>
              <div style={{ flex: '1 1 130px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>End Date</label>
                <input type="date" value={incomeEnd} onChange={e => { setIncomeEnd(e.target.value); setIncomeResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none' }} />
              </div>
              <div style={{ flex: '1 1 150px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Payment Method</label>
                <select value={incomeMethod} onChange={e => { setIncomeMethod(e.target.value); setIncomeResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}>
                  <option value="all">All Methods</option>
                  <option value="cash">Cash</option>
                  <option value="credit card">Credit Card</option>
                  <option value="insurance">Insurance</option>
                  <option value="cheque">Cheque</option>
                  <option value="bank transfer">Bank Transfer</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div style={{ flex: '1 1 140px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Payment Status</label>
                <select value={incomeStatus} onChange={e => { setIncomeStatus(e.target.value); setIncomeResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}>
                  <option value="all">All Statuses</option>
                  <option value="SUCCESS">SUCCESS</option>
                  <option value="REVERSED">REVERSED</option>
                  <option value="PENDING">PENDING</option>
                </select>
              </div>
              <div style={{ flex: '1 1 170px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Provider (Doctor)</label>
                <select value={incomeDoctorId} onChange={e => { setIncomeDoctorId(e.target.value); setIncomeResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}>
                  <option value="all">All Providers</option>
                  {incomeDoctors.map(d => (
                    <option key={d.id} value={d.id}>{[d.prefix, d.fullName, d.shortName].filter(Boolean).join(' ')}</option>
                  ))}
                </select>
              </div>
              {allBranchSettings && allBranchSettings.length > 1 && (
                <div style={{ flex: '1 1 160px' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Branch</label>
                  <select value={financialBranchId} onChange={e => { setFinancialBranchId(e.target.value); setIncomeResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}>
                    <option value="all">All Branches</option>
                    {allBranchSettings.map((b, i) => (
                      <option key={b.id} value={String(b.id)}>Branch {i + 1}{b.official_name ? ` — ${b.official_name}` : ''}</option>
                    ))}
                  </select>
                </div>
              )}
              <button
                onClick={runIncomeReport}
                disabled={!incomeStart || !incomeEnd || incomeLoading}
                style={{ height: 36, padding: '0 20px', borderRadius: 8, border: 'none', background: (!incomeStart || !incomeEnd) ? '#e8ecf0' : '#4680ff', color: (!incomeStart || !incomeEnd) ? '#aaa' : '#fff', fontSize: 13, fontWeight: 600, cursor: (!incomeStart || !incomeEnd) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
              >
                <i className={`ti ${incomeLoading ? 'ti-loader-2' : 'ti-search'}`} />
                {incomeLoading ? 'Loading…' : 'Generate'}
              </button>
            </div>

            {/* Results */}
            <div style={{ padding: '0 24px 24px' }}>
              {incomeLoading && <div style={{ textAlign: 'center', padding: '32px 0', color: COLORS.gray, fontSize: 13 }}>Fetching payments…</div>}

              {!incomeLoading && incomeResults && (
                <>
                  {/* Summary */}
                  <div style={{ display: 'flex', gap: 12, padding: '14px 0 10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ background: '#4680ff12', border: '1px solid #4680ff33', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Payments: </span>
                      <span style={{ fontWeight: 700, color: '#4680ff' }}>{incomeResults.count}</span>
                    </div>
                    <div style={{ background: `${COLORS.green}12`, border: `1px solid ${COLORS.green}44`, borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Total Amount: </span>
                      <span style={{ fontWeight: 700, color: COLORS.green }}>{Number(incomeResults.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {incomeResults.byMethod && Object.entries(incomeResults.byMethod).map(([method, amt]) => (
                      <div key={method} style={{ background: '#f8f9fb', border: '1px solid #e8ecf0', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                        <span style={{ color: COLORS.gray, textTransform: 'capitalize' }}>{method}: </span>
                        <span style={{ fontWeight: 700, color: '#374151' }}>{Number(amt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    ))}
                    {incomeResults.data.length > 0 && (
                      <button onClick={exportIncomeExcel} style={{ marginLeft: 'auto', height: 32, padding: '0 14px', borderRadius: 8, border: '1px solid #4680ff44', background: '#4680ff12', color: '#4680ff', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i className="ti ti-file-spreadsheet" style={{ fontSize: 15 }} />Export Excel
                      </button>
                    )}
                  </div>

                  {incomeResults.data.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '28px 0', color: '#bbb', fontSize: 13 }}>No payments found for the selected filters.</div>
                  ) : (
                    <div style={{ borderRadius: 8, border: '1px solid #e8ecf0' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: 32 }} />
                          <col style={{ width: 96 }} />
                          <col style={{ width: '18%' }} />
                          <col style={{ width: 100 }} />
                          <col style={{ width: 90 }} />
                          <col style={{ width: 110 }} />
                          <col />
                          <col style={{ width: 90 }} />
                          <col style={{ width: '15%' }} />
                        </colgroup>
                        <thead>
                          <tr style={{ background: '#f0f4ff' }}>
                            {[
                              { h: '#', align: 'left' }, { h: 'Date', align: 'left' }, { h: 'Patient', align: 'left' },
                              { h: 'Invoice #', align: 'left' }, { h: 'Amount', align: 'right' }, { h: 'Method', align: 'left' },
                              { h: 'Transaction ID', align: 'left' }, { h: 'Status', align: 'center' }, { h: 'Provider', align: 'left' },
                            ].map(({ h, align }) => (
                              <th key={h} style={{ padding: '8px 8px', textAlign: align, fontWeight: 700, color: '#4680ff', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10, borderBottom: '1px solid #ccd8ff', whiteSpace: 'nowrap', overflow: 'hidden' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {incomeResults.data.map((row, i) => {
                            const isReversed = row.payment_status === 'REVERSED'
                            return (
                              <tr key={row.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc', opacity: isReversed ? 0.6 : 1 }}>
                                <td style={{ padding: '7px 8px', color: '#9aa0ac', borderBottom: '1px solid #f0f2f5' }}>{i + 1}</td>
                                <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', whiteSpace: 'nowrap' }}>{row.payment_date ? new Date(row.payment_date).toLocaleDateString('en-GB') : '—'}</td>
                                <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', wordBreak: 'break-word' }}>{row.patientName || '—'}</td>
                                <td style={{ padding: '7px 8px', color: '#5B6B79', borderBottom: '1px solid #f0f2f5', whiteSpace: 'nowrap' }}>{row.invoice_no || '—'}</td>
                                <td style={{ padding: '7px 8px', color: COLORS.green, fontWeight: 600, borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(row.payment_amount || 0).toFixed(2)}</td>
                                <td style={{ padding: '7px 8px', borderBottom: '1px solid #f0f2f5' }}>
                                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: row.payment_method?.toLowerCase() === 'cash' ? '#e8faf3' : row.payment_method?.toLowerCase() === 'credit card' ? '#e8eeff' : '#f5f0ff', color: row.payment_method?.toLowerCase() === 'cash' ? COLORS.green : row.payment_method?.toLowerCase() === 'credit card' ? '#4680ff' : '#7c3aed', textTransform: 'capitalize' }}>
                                    {row.payment_method || '—'}
                                  </span>
                                </td>
                                <td style={{ padding: '7px 8px', color: '#5B6B79', borderBottom: '1px solid #f0f2f5', wordBreak: 'break-all', fontSize: 11 }}>{row.transactionId || '—'}</td>
                                <td style={{ padding: '7px 8px', borderBottom: '1px solid #f0f2f5', textAlign: 'center' }}>
                                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: isReversed ? '#fff0f0' : row.payment_status === 'PENDING' ? '#fff8e1' : '#e8faf3', color: isReversed ? COLORS.red : row.payment_status === 'PENDING' ? '#b45309' : COLORS.green }}>
                                    {row.payment_status || '—'}
                                  </span>
                                </td>
                                <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', wordBreak: 'break-word' }}>{row.doctorName || '—'}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: '#eef2ff' }}>
                            <td colSpan={4} style={{ padding: '8px 10px', fontWeight: 700, color: COLORS.gray, fontSize: 11, textTransform: 'uppercase' }}>Total ({incomeResults.count} payments)</td>
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: COLORS.green, textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(incomeResults.total).toFixed(2)}</td>
                            <td colSpan={4} />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Invoices Report Modal ── */}
      {invoicesModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 40, overflowY: 'auto' }} onClick={() => setInvoicesModal(false)}>
          <div style={{ background: '#fff', borderRadius: 12, width: '98%', maxWidth: 1400, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', marginBottom: 40 }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 24px', borderBottom: '1px solid #e8ecf0' }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: '#4680ff20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="ti ti-file-invoice" style={{ color: '#4680ff', fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Invoices Report</div>
                <div style={{ fontSize: 11, color: COLORS.gray }}>All invoices from patient_invoices with flexible filtering</div>
              </div>
              <button onClick={() => setInvoicesModal(false)} style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14 }}>
                <i className="ti ti-x" />
              </button>
            </div>

            {/* Filters */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f2f5', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 130px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Start Date</label>
                <input type="date" value={invoicesStart} onChange={e => { setInvoicesStart(e.target.value); setInvoicesResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none' }} />
              </div>
              <div style={{ flex: '1 1 130px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>End Date</label>
                <input type="date" value={invoicesEnd} onChange={e => { setInvoicesEnd(e.target.value); setInvoicesResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none' }} />
              </div>
              <div style={{ flex: '1 1 150px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Invoice Status</label>
                <select value={invoicesStatus} onChange={e => { setInvoicesStatus(e.target.value); setInvoicesResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}>
                  <option value="all">All Invoice Statuses</option>
                  <option value="not paid">Not Paid</option>
                  <option value="partially paid">Partially Paid</option>
                  <option value="fully paid">Fully Paid</option>
                </select>
              </div>
              <div style={{ flex: '1 1 140px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Status</label>
                <select value={invoicesRecordStatus} onChange={e => { setInvoicesRecordStatus(e.target.value); setInvoicesResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}>
                  <option value="all">All Statuses</option>
                  <option value="remaining">Remaining</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div style={{ flex: '1 1 170px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Created By</label>
                <select value={invoicesUserId} onChange={e => { setInvoicesUserId(e.target.value); setInvoicesResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}>
                  <option value="all">All Users</option>
                  {invoicesUsers.map(u => (
                    <option key={u.userId} value={u.userId}>{u.displayName}</option>
                  ))}
                </select>
              </div>
              {allBranchSettings && allBranchSettings.length > 1 && (
                <div style={{ flex: '1 1 160px' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 5 }}>Branch</label>
                  <select value={financialBranchId} onChange={e => { setFinancialBranchId(e.target.value); setInvoicesResults(null) }} style={{ width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #dde3eb', padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}>
                    <option value="all">All Branches</option>
                    {allBranchSettings.map((b, i) => (
                      <option key={b.id} value={String(b.id)}>Branch {i + 1}{b.official_name ? ` — ${b.official_name}` : ''}</option>
                    ))}
                  </select>
                </div>
              )}
              <button
                onClick={runInvoicesReport}
                disabled={!invoicesStart || !invoicesEnd || invoicesLoading}
                style={{ height: 36, padding: '0 20px', borderRadius: 8, border: 'none', background: (!invoicesStart || !invoicesEnd) ? '#e8ecf0' : '#4680ff', color: (!invoicesStart || !invoicesEnd) ? '#aaa' : '#fff', fontSize: 13, fontWeight: 600, cursor: (!invoicesStart || !invoicesEnd) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
              >
                <i className={`ti ${invoicesLoading ? 'ti-loader-2' : 'ti-search'}`} />
                {invoicesLoading ? 'Loading…' : 'Generate'}
              </button>
            </div>

            {/* Results */}
            <div style={{ padding: '0 24px 24px' }}>
              {invoicesLoading && <div style={{ textAlign: 'center', padding: '32px 0', color: COLORS.gray, fontSize: 13 }}>Fetching invoices…</div>}

              {!invoicesLoading && invoicesResults && (
                <>
                  {/* Summary */}
                  <div style={{ display: 'flex', gap: 12, padding: '14px 0 10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ background: '#4680ff12', border: '1px solid #4680ff33', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Invoices: </span>
                      <span style={{ fontWeight: 700, color: '#4680ff' }}>{invoicesResults.count}</span>
                    </div>
                    <div style={{ background: `${COLORS.green}12`, border: `1px solid ${COLORS.green}44`, borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Total Invoiced: </span>
                      <span style={{ fontWeight: 700, color: COLORS.green }}>{Number(invoicesResults.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ background: '#f8f9fb', border: '1px solid #e8ecf0', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Total Paid: </span>
                      <span style={{ fontWeight: 700, color: '#374151' }}>{Number(invoicesResults.totalPaid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ background: `${COLORS.red}12`, border: `1px solid ${COLORS.red}44`, borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Outstanding: </span>
                      <span style={{ fontWeight: 700, color: COLORS.red }}>{Number(invoicesResults.totalRemaining || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ background: '#f8f9fb', border: '1px solid #e8ecf0', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                      <span style={{ color: COLORS.gray }}>Total VAT: </span>
                      <span style={{ fontWeight: 700, color: '#374151' }}>{Number(invoicesResults.totalVat || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {invoicesResults.data.length > 0 && (
                      <button onClick={exportInvoicesExcel} style={{ marginLeft: 'auto', height: 32, padding: '0 14px', borderRadius: 8, border: '1px solid #4680ff44', background: '#4680ff12', color: '#4680ff', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i className="ti ti-file-spreadsheet" style={{ fontSize: 15 }} />Export Excel
                      </button>
                    )}
                  </div>

                  {invoicesResults.data.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '28px 0', color: '#bbb', fontSize: 13 }}>No invoices found for the selected filters.</div>
                  ) : (
                    <div style={{ borderRadius: 8, border: '1px solid #e8ecf0' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: 32 }} />
                          <col style={{ width: 96 }} />
                          <col style={{ width: 100 }} />
                          <col style={{ width: '15%' }} />
                          <col style={{ width: 90 }} />
                          <col style={{ width: 80 }} />
                          <col style={{ width: 90 }} />
                          <col style={{ width: 90 }} />
                          <col style={{ width: 90 }} />
                          <col style={{ width: 110 }} />
                          <col style={{ width: '14%' }} />
                        </colgroup>
                        <thead>
                          <tr style={{ background: '#f0f4ff' }}>
                            {[
                              { h: '#', align: 'left' }, { h: 'Date', align: 'left' }, { h: 'Invoice #', align: 'left' },
                              { h: 'Patient', align: 'left' }, { h: 'Total', align: 'right' }, { h: 'VAT', align: 'right' }, { h: 'Paid', align: 'right' },
                              { h: 'Remaining', align: 'right' }, { h: 'Status', align: 'center' }, { h: 'Invoice Status', align: 'center' },
                              { h: 'By', align: 'left' },
                            ].map(({ h, align }) => (
                              <th key={h} style={{ padding: '8px 8px', textAlign: align, fontWeight: 700, color: '#4680ff', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10, borderBottom: '1px solid #ccd8ff', whiteSpace: 'nowrap', overflow: 'hidden' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {invoicesResults.data.map((row, i) => {
                            const invStatus = row.invoice_status
                            const invStatusNorm = (invStatus || '').toLowerCase()
                            const statusColor = invStatusNorm === 'fully paid' ? COLORS.green : invStatusNorm === 'partially paid' ? '#b45309' : COLORS.red
                            const statusBg = invStatusNorm === 'fully paid' ? '#e8faf3' : invStatusNorm === 'partially paid' ? '#fff8e1' : '#fff0f0'
                            return (
                              <tr key={row.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                <td style={{ padding: '7px 8px', color: '#9aa0ac', borderBottom: '1px solid #f0f2f5' }}>{i + 1}</td>
                                <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', whiteSpace: 'nowrap' }}>{row.invoice_date ? new Date(row.invoice_date).toLocaleDateString('en-GB') : '—'}</td>
                                <td style={{ padding: '7px 8px', color: '#5B6B79', borderBottom: '1px solid #f0f2f5', whiteSpace: 'nowrap' }}>{row.invoice_no || '—'}</td>
                                <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', maxWidth: 0 }}>
                                  {row.patientName || '—'}
                                  {row.fileNumber && <div style={{ fontSize: 10, color: '#9aa0ac' }}>#{row.fileNumber}</div>}
                                </td>
                                <td style={{ padding: '7px 8px', color: '#374151', fontWeight: 600, borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(row.total_amount || 0).toFixed(2)}</td>
                                <td style={{ padding: '7px 8px', color: '#5B6B79', borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(row.vat_amount || 0).toFixed(2)}</td>
                                <td style={{ padding: '7px 8px', color: COLORS.green, fontWeight: 600, borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(row.paid_amount || 0).toFixed(2)}</td>
                                <td style={{ padding: '7px 8px', color: Number(row.remaining_amount) > 0 ? COLORS.red : '#9aa0ac', fontWeight: 600, borderBottom: '1px solid #f0f2f5', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(row.remaining_amount || 0).toFixed(2)}</td>
                                <td style={{ padding: '7px 8px', borderBottom: '1px solid #f0f2f5', textAlign: 'center', textTransform: 'capitalize', color: '#5B6B79' }}>{row.status || '—'}</td>
                                <td style={{ padding: '7px 8px', borderBottom: '1px solid #f0f2f5', textAlign: 'center' }}>
                                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: statusBg, color: statusColor, textTransform: 'capitalize' }}>
                                    {invStatus || '—'}
                                  </span>
                                </td>
                                <td style={{ padding: '7px 8px', color: '#374151', borderBottom: '1px solid #f0f2f5', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', maxWidth: 0 }}>{row.createdByName || '—'}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: '#eef2ff' }}>
                            <td colSpan={4} style={{ padding: '8px 10px', fontWeight: 700, color: COLORS.gray, fontSize: 11, textTransform: 'uppercase' }}>Total ({invoicesResults.count} invoices)</td>
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: '#374151', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(invoicesResults.totalAmount).toFixed(2)}</td>
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: '#5B6B79', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(invoicesResults.totalVat || 0).toFixed(2)}</td>
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: COLORS.green, textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(invoicesResults.totalPaid).toFixed(2)}</td>
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: COLORS.red, textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(invoicesResults.totalRemaining).toFixed(2)}</td>
                            <td colSpan={3} />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
